import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    Input,
    ChangeDetectorRef,
    EventEmitter,
    Output,
} from '@angular/core';
import { GetChargingStationResponse, PowerModuleDetails, SatelliteMetrics } from 'api/charging_stations.api';
import { isEmpty as _isEmpty, PropertyPath, set as _set, get as _get } from 'lodash';
import { AuthService } from 'providers/auth.service';

export interface Pmc {
    canRelative: number;
    cabId: number;
    signedCabId: number;
    side: 'A' | 'B';
    slot: number;
    status: SatelliteMetrics['pmcStatus'][0];
    device: GetChargingStationResponse['devices'][0] & { details: PowerModuleDetails };
    canAddress?: number;
    operatingHours?: number;
    operatingHoursSince?: string;
}

export interface PmcAlert {
    severity: string;
    problem: string;
}
export type MappedBySeverity = {
    [severity: string]: string[];
};
interface PmcAlertsByCanRelative {
    [canRelative: number]: MappedBySeverity;
}

export const NON_FATAL_PMC_PROBLEMS = ['GROUND_FAULT', 'RUNTIME_PMC_TEMPERATURE_SENSOR_BROKEN'];

const AllColumns = [
    'counters',
    'channel',
    'origin',
    'moduleSerial',
    'generation',
    'canSerial',
    'maxVoltage',
    'status',
    'present',
    'operatingHours',
    'manufacturingDate',
    'boardRevision',
    'can',
    'dcVoltage',
    'overruns',
    'appVersion',
    'bootVersion',
    'alerts',
    'flags',
];

@Component({
    selector: 'ce-pmc-list',
    templateUrl: './pmc-list.component.html',
    styleUrls: ['./pmc-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('pmcExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class PmcListComponent implements OnInit {
    @Input() pmcStatus: SatelliteMetrics['pmcStatus'];
    @Input() pmcDevices: GetChargingStationResponse['devices'];

    @Input() hoveringPmcCanRelativeByCabId: { [cabId: string]: number[] } = {};
    @Output() hoveringPmcCanRelativeChangeForPmcList = new EventEmitter<Pmc>();

    columnsToDisplay = [...AllColumns];
    allFatalPmcAlerts = [];

    pmc: Pmc[];
    /** Alerts mapped by canRelative & severity */
    pmcAlerts: PmcAlertsByCanRelative = {};
    /** Fatal alerts mapped by canRelative & bundled in one string array */
    fatalPmcAlerts: { [serialNumber: string]: string[] } = {};
    /** Pmc alerts severity levels */
    severityLevels: string[] = [];

    /** Current expanded pmc row (if have) */
    expandedPmc: Pmc;

    isSystemAdmin = false;

    constructor(
        private cdRef: ChangeDetectorRef,
        private auth: AuthService
    ) {}

    ngOnInit(): void {
        this.isSystemAdmin = this.auth.getPermissions().isSystemAdmin;
    }

    ngOnChanges() {
        if (this.pmcStatus && this.pmcDevices) {
            const getCounterValue = (pmcStatus: SatelliteMetrics['pmcStatus'][0], key: string) =>
                pmcStatus?.counters?.[key]?.value;
            const getCounterSince = (pmcStatus: SatelliteMetrics['pmcStatus'][0], key: string) =>
                pmcStatus?.counters?.[key]?.started
                    ? new Date(pmcStatus?.counters?.[key]?.started * 1000).toISOString()
                    : undefined;
            this.pmc = this.pmcStatus.map(
                (pmcStatus) =>
                    ({
                        ...extractPmcCanId(pmcStatus.canAddress),
                        status: pmcStatus,
                        device: this.pmcDevices.find(
                            (d) => +(d.details as PowerModuleDetails)?.can === pmcStatus.canAddress
                        ),
                        operatingHours: getCounterValue(pmcStatus, 'Active_s') / 60 / 60 || undefined,
                        operatingHoursSince: getCounterSince(pmcStatus, 'Active_s'),
                    }) as Pmc
            );
        } else if (this.pmcDevices) {
            this.pmc = this.pmcDevices.map(
                (d) =>
                    ({
                        ...extractPmcCanId(+(d.details as PowerModuleDetails)?.can),
                        status: undefined,
                        device: d,
                    }) as Pmc
            );
        }

        // if no devices with status field (=older fw version), hide some fields
        if (this.pmc.some((p) => p.status)) {
            this.columnsToDisplay = AllColumns;
        } else {
            this.columnsToDisplay = AllColumns.filter(
                (c) => !['status', 'dcVoltage', 'overruns', 'present'].includes(c)
            );
        }

        // pmc alerts
        const { alerts, severityLevels } = mapPmcAlerts<PmcAlertsByCanRelative>(
            this.pmc,
            undefined,
            'canRelative',
            true
        );
        this.pmcAlerts = alerts;
        this.severityLevels = severityLevels;
        this.pmc.forEach((m) => {
            const _alerts = (m?.device?.details as any)?.alerts || [];

            const _fatal = [];
            _alerts.forEach((a: PmcAlert) => {
                if (this.isSystemAdmin || !NON_FATAL_PMC_PROBLEMS.includes(a?.problem)) {
                    _fatal.push(a?.problem);
                }
            });

            this.fatalPmcAlerts[m?.device?.serialNumber] = _fatal;
        });

        this.cdRef.markForCheck();
    }

    generatePmcOrigin(cabId: any): string {
        if (typeof cabId === 'number') {
            if (cabId === 0) {
                return 'Main';
            } else {
                return `Secondary ${cabId}`;
            }
        } else {
            return '';
        }
    }

    toggleExpandedPmc(pmc: Pmc) {
        // Ease out the checking by checking only the canAddress and signedCabId
        if (this.isSameExpandedPmc(pmc)) {
            this.expandedPmc = null;
        } else {
            this.expandedPmc = pmc;
        }

        this.cdRef.markForCheck();
    }
    isSameExpandedPmc(pmc: Pmc) {
        const isSameCanAddress = pmc?.canAddress === this.expandedPmc?.canAddress;
        const isSameSignedCabId = pmc?.signedCabId === this.expandedPmc?.signedCabId;

        return isSameCanAddress && isSameSignedCabId;
    }

    pmcListTrackBy(index: number, pmc: Pmc) {
        return `${pmc?.signedCabId}#${pmc?.canAddress}`;
    }
}

export function extractPmcCanId(canAddress: number) {
    const cabId = Math.floor(canAddress / 100);
    const cabLabel = cabId > 0 ? `SC${cabId}` : 'MC';
    const signedCabId = cabId > 0 ? -cabId : cabId; // secondary cabinet ids are negative
    const canRelative = canAddress % 100;
    const side = (canRelative - 2) % 2 ? 'B' : 'A';
    const slot = Math.floor((canRelative - 2) / 2) + 1;
    const label = `${slot}${side}`;

    return { cabId, signedCabId, cabLabel, canRelative, side, slot, label, canAddress };
}

/** Map pmc alerts by an identifier (e.g: canRelative / can) and optionally grouped by severity.
 * - With groupBySeverity, returned alerts interface
 * -- { [can: number]: { [severity: string] : string[] } }
 * - Without groupBySeverity, returned alerts interface
 * -- { [can: number]: string[] }
 * - Leaving pathToIdentifier as undefined to get an array of all unique alerts
 * -- { all : string[] }
 */
export function mapPmcAlerts<T>(
    pmcs: any[],
    pathToAlerts: PropertyPath,
    pathToIdentifier: PropertyPath,
    groupBySeverity = true,
    useAbsoluteCan = false
): { alerts: T; severityLevels: string[]; allUniqueAlerts: string[] } {
    const _mapped = {};
    const _severityLevels = [];
    const _allUniqueAlerts = [];

    if (!_isEmpty(pmcs)) {
        pmcs.forEach((pmc) => {
            const alerts: PmcAlert[] = _get(pmc, pathToAlerts || 'device.details.alerts', []);
            const _mappedBy: string = _get(pmc, pathToIdentifier, 'all');
            const mappedBy: string = useAbsoluteCan ? (Number(_mappedBy) % 100).toString() : _mappedBy;

            if (!_isEmpty(alerts)) {
                alerts.forEach((alert) => {
                    const existedAlerts = _mapped[mappedBy];

                    if (groupBySeverity) {
                        if (existedAlerts && existedAlerts[alert?.severity]) {
                            if (!existedAlerts[alert?.severity].some((existed: string) => existed === alert?.problem)) {
                                existedAlerts[alert?.severity].push(alert?.problem);
                            }
                        } else {
                            _set(_mapped, [mappedBy, `${alert?.severity}`], [alert?.problem]);
                        }
                    } else {
                        if (existedAlerts && !existedAlerts.some((existed: string) => existed === alert?.problem)) {
                            existedAlerts.push(alert?.problem);
                        } else {
                            _set(_mapped, [mappedBy], [alert?.problem]);
                        }
                    }

                    if (alert?.severity && !_severityLevels.includes(alert?.severity)) {
                        _severityLevels.push(alert?.severity);
                    }

                    if (alert?.problem && !_allUniqueAlerts.includes(alert?.problem)) {
                        _allUniqueAlerts.push(alert?.problem);
                    }
                });
            }
        });
    }

    return { alerts: _mapped as T, severityLevels: _severityLevels, allUniqueAlerts: _allUniqueAlerts };
}
